import React, { useMemo, useState, useRef, useEffect } from 'react';
import BarChart from "@cloudscape-design/components/bar-chart";
import { Card } from "@mui/material";

const ActivityCharts = ({ activityData }) => {
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [selectedActions, setSelectedActions] = useState({
    start: true,
    stop: true,
    change: true
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Process data for user activity chart
  const userActivityData = useMemo(() => {
    // ... (keeping the existing data processing logic)
    const userStats = {};
    const allAccounts = new Set();
    
    activityData.forEach(activity => {
      const actionLower = activity.action.toLowerCase();
      
      if (!userStats[activity.username]) {
        userStats[activity.username] = {
          starts: 0,
          stops: 0,
          changes: 0,
          byAccount: {}
        };
      }
      
      const accounts = activity.accounts.split(',');
      accounts.forEach(account => {
        const trimmedAccount = account.trim();
        allAccounts.add(trimmedAccount);
        
        if (!userStats[activity.username].byAccount[trimmedAccount]) {
          userStats[activity.username].byAccount[trimmedAccount] = {
            starts: 0,
            stops: 0,
            changes: 0
          };
        }
        
        if (actionLower.includes('start')) {
          userStats[activity.username].starts += 1;
          userStats[activity.username].byAccount[trimmedAccount].starts += 1;
        } else if (actionLower.includes('stop')) {
          userStats[activity.username].stops += 1;
          userStats[activity.username].byAccount[trimmedAccount].stops += 1;
        } else if (actionLower.includes('change')) {
          userStats[activity.username].changes += 1;
          userStats[activity.username].byAccount[trimmedAccount].changes += 1;
        }
      });
    });

    // Create series based on selected account
    let data;
    if (selectedAccount === 'all') {
      data = [
        {
          title: "Start",
          type: "bar",
          data: Object.entries(userStats).map(([username, stats]) => ({
            x: username,
            y: stats.starts
          })),
          valueFormatter: value => `${value}`,
          color: "#99CCFF"
        },
        {
          title: "Stop",
          type: "bar",
          data: Object.entries(userStats).map(([username, stats]) => ({
            x: username,
            y: stats.stops
          })),
          valueFormatter: value => `${value}`,
          color: "#B8D8FF"
        },
        {
          title: "Schedule Change",
          type: "bar",
          data: Object.entries(userStats).map(([username, stats]) => ({
            x: username,
            y: stats.changes
          })),
          valueFormatter: value => `${value}`,
          color: "#D6E9FF"
        }
      ];
    } else {
      data = [
        {
          title: "Start",
          type: "bar",
          data: Object.entries(userStats).map(([username, stats]) => ({
            x: username,
            y: stats.byAccount[selectedAccount]?.starts || 0
          })),
          valueFormatter: value => `${value}`,
          color: "#A3C2E6"
        },
        {
          title: "Stop",
          type: "bar",
          data: Object.entries(userStats).map(([username, stats]) => ({
            x: username,
            y: stats.byAccount[selectedAccount]?.stops || 0
          })),
          valueFormatter: value => `${value}`,
          color: "#E6EEF7"
        },
        {
          title: "Schedule Change",
          type: "bar",
          data: Object.entries(userStats).map(([username, stats]) => ({
            x: username,
            y: stats.byAccount[selectedAccount]?.changes || 0
          })),
          valueFormatter: value => `${value}`,
          color: "#CCE0F5"
        }
      ];
    }

    const maxValue = Math.max(
      ...data.flatMap(series => series.data.map(d => d.y)),
      1
    );

    return {
      series: data,
      xDomain: Object.keys(userStats),
      yDomain: [0, maxValue + 2],
      accounts: ['all', ...Array.from(allAccounts)]
    };
  }, [activityData, selectedAccount]);

  // Process data for account activity chart
  const accountActivityData = useMemo(() => {
    // ... (keeping the existing data processing logic)
    const accountStats = {};
    
    activityData.forEach(activity => {
      const actionLower = activity.action.toLowerCase();
      
      const accounts = activity.accounts.split(',');
      accounts.forEach(account => {
        const trimmedAccount = account.trim();
        if (!accountStats[trimmedAccount]) {
          accountStats[trimmedAccount] = { starts: 0, stops: 0, changes: 0 };
        }
        
        if (actionLower.includes('start')) {
          accountStats[trimmedAccount].starts += 1;
        } else if (actionLower.includes('stop')) {
          accountStats[trimmedAccount].stops += 1;
        } else if (actionLower.includes('change')) {
          accountStats[trimmedAccount].changes += 1;
        }
      });
    });

    const series = [];
    if (selectedActions.start) {
      series.push({
        title: "Start",
        type: "bar",
        data: Object.entries(accountStats).map(([account, stats]) => ({
          x: account,
          y: stats.starts
        })),
        valueFormatter: value => `${value}`,
        color: "#A3C2E6"
      });
    }
    if (selectedActions.stop) {
      series.push({
        title: "Stop",
        type: "bar",
        data: Object.entries(accountStats).map(([account, stats]) => ({
          x: account,
          y: stats.stops
        })),
        valueFormatter: value => `${value}`,
        color: "#E6EEF7"
      });
    }
    if (selectedActions.change) {
      series.push({
        title: "Schedule Change",
        type: "bar",
        data: Object.entries(accountStats).map(([account, stats]) => ({
          x: account,
          y: stats.changes
        })),
        valueFormatter: value => `${value}`,
        color: "#CCE0F5"
      });
    }

    const maxValue = Math.max(
      ...Object.values(accountStats).map(stats => 
        Math.max(
          selectedActions.start ? stats.starts : 0,
          selectedActions.stop ? stats.stops : 0,
          selectedActions.change ? stats.changes : 0
        )
      ),
      1
    );
    
    return {
      series,
      xDomain: Object.keys(accountStats),
      yDomain: [0, maxValue + 2]
    };
  }, [activityData, selectedActions]);

  const handleAccountSelect = (account) => {
    setSelectedAccount(account);
    setIsDropdownOpen(false);
  };

  return (
    <>
      {/* Custom CSS for primary color styling */}
      <style jsx>{`
        .custom-select {
          appearance: none;
          background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23003366' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: right 0.7rem center;
          background-size: 1.3em auto;
          padding-right: 2.5rem;
        }
        
        .custom-dropdown {
          position: relative;
        }
        
        .custom-dropdown-button {
          min-width: 200px;
          width: auto;
          padding: 0.5rem 0.75rem;
          background-color: white;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
          font-size: 0.875rem;
          color: #374151;
          text-align: left;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: space-between;
          white-space: nowrap;
          gap: 0.5rem;
        }
        
        .custom-dropdown-text {
          flex: 1;
          text-align: left;
        }
        
        .custom-dropdown-button:hover {
          border-color: #003366;
          box-shadow: 0 1px 3px 0 rgba(0, 51, 102, 0.1), 0 1px 2px 0 rgba(0, 51, 102, 0.06);
        }
        
        .custom-dropdown-button:focus {
          outline: none;
          border-color: #003366;
          box-shadow: 0 0 0 3px rgba(0, 51, 102, 0.1);
        }
        
        .custom-dropdown-arrow {
          width: 1rem;
          height: 1rem;
          color: #003366;
          transition: transform 0.2s ease-in-out;
          flex-shrink: 0;
        }
        
        .custom-dropdown-arrow.open {
          transform: rotate(180deg);
        }
        
        .custom-dropdown-menu {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          z-index: 50;
          margin-top: 0.25rem;
          background-color: white;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          max-height: 12rem;
          overflow-y: auto;
          min-width: 200px;
          width: max-content;
        }
        
        .custom-dropdown-option {
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          color: #374151;
          cursor: pointer;
          border-bottom: 1px solid #f3f4f6;
          white-space: nowrap;
          user-select: none;
        }
        
        .custom-dropdown-option:last-child {
          border-bottom: none;
        }
        
        .custom-dropdown-option:hover {
          background-color: rgba(0, 51, 102, 0.1);
          color: #003366;
        }
        
        .custom-dropdown-option.selected {
          background-color: #003366;
          color: white;
        }
        
        .custom-checkbox {
          appearance: none;
          background-color: #fff;
          margin: 0;
          font: inherit;
          color: #003366;
          width: 1rem;
          height: 1rem;
          border: 2px solid #d1d5db;
          border-radius: 0.25rem;
          transform: translateY(-0.075em);
          display: grid;
          place-content: center;
        }
        
        .custom-checkbox::before {
          content: "";
          width: 0.65em;
          height: 0.65em;
          clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
          transform: scale(0);
          transform-origin: bottom left;
          transition: 120ms transform ease-in-out;
          box-shadow: inset 1em 1em #003366;
          background-color: #003366;
        }
        
        .custom-checkbox:checked {
          border-color: #003366;
        }
        
        .custom-checkbox:checked::before {
          transform: scale(1);
        }
        
        .custom-checkbox:focus {
          outline: 2px solid #003366;
          outline-offset: 2px;
        }
        
        .custom-checkbox:hover {
          border-color: #003366;
        }
      `}</style>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-2">
        {/* User Activity Chart */}
        <Card className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-[#003366]">User Activity</h3>
            <div className="custom-dropdown" ref={dropdownRef}>
              <button
                type="button"
                className="custom-dropdown-button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <span className="custom-dropdown-text">
                  {selectedAccount === 'all' ? 'All Accounts' : selectedAccount}
                </span>
                <svg 
                  className={`custom-dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isDropdownOpen && (
                <div className="custom-dropdown-menu">
                  {userActivityData.accounts.map(account => (
                    <div
                      key={account}
                      className={`custom-dropdown-option ${selectedAccount === account ? 'selected' : ''}`}
                      onClick={() => handleAccountSelect(account)}
                    >
                      {account === 'all' ? 'All Accounts' : account}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div>
            <BarChart
              series={userActivityData.series}
              xDomain={userActivityData.xDomain}
              yDomain={userActivityData.yDomain}
              height={250}
              hideFilter={true}
              xTitle="Users"
              yTitle="Number of Activities"
              i18nStrings={{
                xTickFormatter: (value) => value,
                yTickFormatter: (value) => Math.round(value)
              }}
            />
          </div>
        </Card>

        {/* Account Activity Chart */}
        <Card className="p-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <h3 className="text-lg font-semibold text-[#003366]">Account Activity</h3>
            <div className="flex flex-wrap gap-4">
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedActions.start}
                  onChange={(e) => setSelectedActions(prev => ({
                    ...prev,
                    start: e.target.checked
                  }))}
                  className="custom-checkbox"
                />
                <span className="ml-2 text-sm text-gray-700">Start</span>
              </label>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedActions.stop}
                  onChange={(e) => setSelectedActions(prev => ({
                    ...prev,
                    stop: e.target.checked
                  }))}
                  className="custom-checkbox"
                />
                <span className="ml-2 text-sm text-gray-700">Stop</span>
              </label>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedActions.change}
                  onChange={(e) => setSelectedActions(prev => ({
                    ...prev,
                    change: e.target.checked
                  }))}
                  className="custom-checkbox"
                />
                <span className="ml-2 text-sm text-gray-700">Schedule Change</span>
              </label>
            </div>
          </div>
          <BarChart
            series={accountActivityData.series}
            xDomain={accountActivityData.xDomain}
            yDomain={accountActivityData.yDomain}
            hideFilter={true}
            height={250}
            xTitle="AWS Accounts"
            yTitle="Number of Events"
            i18nStrings={{
              xTickFormatter: (value) => value,
              yTickFormatter: (value) => Math.round(value)
            }}
          />
        </Card>
      </div>
    </>
  );
};

export default ActivityCharts;